import { useEffect, useState } from "react";
import { Calendar, UserPlus, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { supabase } from "../../../lib/supabase";

export function LandingPage() {
  const navigate = useNavigate();
  const [userName, setUserName] = useState<string>("");
  const [userRoles, setUserRoles] = useState<string[]>([]);

  useEffect(() => {
    const getUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user?.email && window.location.pathname !== "/auth/login") {
        setUserName(user.email.split("@")[0]);

        // Fetch user roles
        const { data: roles, error } = await supabase
          .from("user_roles")
          .select("role_name")
          .eq("user_uuid", user.id);

        if (!error && roles) {
          setUserRoles(roles.map((role) => role.role_name));
        }
      } else {
        setUserName("");
        setUserRoles([]);
      }
    };
    getUser();

    const { data: authListener } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        setUserName("");
        setUserRoles([]);
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-16 text-center">
        <h1 className="mb-6 bg-gradient-to-r from-[#4CAF50] to-[#45a049] bg-clip-text text-5xl font-bold text-transparent">
          VINCER Coordinator Dashboard
        </h1>
      </div>

      <div className="mb-16 flex items-center gap-6 rounded-xl border bg-white p-6">
        <div className="rounded-lg bg-[#E8F5E9] p-4">
          <Users className="size-8 text-[#4CAF50]" />
        </div>
        <div>
          <h2 className="mb-2 text-2xl font-semibold">
            Welcome back{userName ? `, ${userName}` : ""}! 👋
          </h2>
          <p className="text-gray-600">
            Ready to make some meaningful connections today? Manage your
            mentors, mentees, and meetings all in one place.
          </p>
          {userRoles.length > 0 && (
            <p className="mt-2 text-sm text-gray-500">
              Your roles: {userRoles.join(", ")}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {/* Add Mentors CTA */}
        <div className="rounded-xl border bg-white p-8 shadow-sm transition-shadow hover:shadow-md">
          <div className="text-center">
            <div className="mb-6 inline-flex size-16 items-center justify-center rounded-full bg-[#E8F5E9]">
              <UserPlus className="size-8 text-[#4CAF50]" />
            </div>
            <h2 className="mb-4 text-2xl font-bold">Add Mentors</h2>
            <p className="mb-6 text-gray-600">
              Bring in experienced professionals to guide and support our
              mentees.
            </p>
            <button
              onClick={() => navigate("/coordinator/mentors")}
              className="w-full rounded-lg bg-[#4CAF50] px-6 py-3 font-medium text-white transition-colors hover:bg-[#45a049]"
            >
              Add New Mentor
            </button>
          </div>
        </div>

        {/* Add Mentees CTA */}
        <div className="rounded-xl border bg-white p-8 shadow-sm transition-shadow hover:shadow-md">
          <div className="text-center">
            <div className="mb-6 inline-flex size-16 items-center justify-center rounded-full bg-[#E8F5E9]">
              <Users className="size-8 text-[#4CAF50]" />
            </div>
            <h2 className="mb-4 text-2xl font-bold">Add Mentees</h2>
            <p className="mb-6 text-gray-600">
              Welcome new participants looking for guidance and growth
              opportunities.
            </p>
            <button
              onClick={() => navigate("/coordinator/mentees")}
              className="w-full rounded-lg bg-[#4CAF50] px-6 py-3 font-medium text-white transition-colors hover:bg-[#45a049]"
            >
              Add New Mentee
            </button>
          </div>
        </div>

        {/* Manage Meetings CTA */}
        <div className="rounded-xl border bg-white p-8 shadow-sm transition-shadow hover:shadow-md">
          <div className="text-center">
            <div className="mb-6 inline-flex size-16 items-center justify-center rounded-full bg-[#E8F5E9]">
              <Calendar className="size-8 text-[#4CAF50]" />
            </div>
            <h2 className="mb-4 text-2xl font-bold">Manage Meetings</h2>
            <p className="mb-6 text-gray-600">
              Schedule and oversee mentorship sessions and track progress.
            </p>
            <button
              onClick={() => navigate("/coordinator/meetings")}
              className="w-full rounded-lg bg-[#4CAF50] px-6 py-3 font-medium text-white transition-colors hover:bg-[#45a049]"
            >
              View Meetings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
